import { createContext, useContext, useState, useCallback } from 'react';

const STORAGE_KEY = 'recently_viewed_products';
const MAX_ITEMS = 12;

const RecentlyViewedContext = createContext(null);

export const useRecentlyViewed = () => {
  const context = useContext(RecentlyViewedContext);
  if (!context) {
    throw new Error('useRecentlyViewed must be used within a RecentlyViewedProvider');
  }
  return context;
};

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Keep only the fields ProductCard needs, so localStorage stays lean.
const trimProduct = (p) => ({
  id: p.id,
  name: p.name,
  image_url: p.image_url,
  images: p.images || [],
  price: p.price,
  original_price: p.original_price ?? null,
  brand: p.brand ?? null,
  stock: p.stock ?? 0,
  avg_rating: p.avg_rating ?? 0,
  rating_count: p.rating_count ?? 0,
  shipping_fee: p.shipping_fee ?? 0,
  is_grouped: p.is_grouped ?? false,
  group_name: p.group_name ?? null,
  variant_count: p.variant_count ?? 0,
});

export const RecentlyViewedProvider = ({ children }) => {
  const [recentlyViewed, setRecentlyViewed] = useState(readStorage);

  const addRecentlyViewed = useCallback((product) => {
    if (!product?.id) return;
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((p) => p.id !== product.id);
      const next = [trimProduct(product), ...filtered].slice(0, MAX_ITEMS);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore quota errors
      }
      return next;
    });
  }, []);

  const clearRecentlyViewed = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setRecentlyViewed([]);
  }, []);

  return (
    <RecentlyViewedContext.Provider value={{ recentlyViewed, addRecentlyViewed, clearRecentlyViewed }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
};
